import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

const sections = [
  { id: "who-we-are", title: "1. Who We Are" },
  { id: "data-we-collect", title: "2. Data We Collect" },
  { id: "how-we-collect", title: "3. How We Collect Data" },
  { id: "how-we-use", title: "4. How We Use Your Data" },
  { id: "legal-basis", title: "5. Legal Basis for Processing" },
  { id: "sharing", title: "6. Sharing Your Data" },
  { id: "retention", title: "7. Retention Periods" },
  { id: "your-rights", title: "8. Your Rights" },
  { id: "cookies", title: "9. Cookies" },
  { id: "transfers", title: "10. International Transfers" },
  { id: "changes", title: "11. Changes to This Policy" },
  { id: "contact", title: "12. Contact Us" },
];

interface SectionProps {
  id: string;
  title: string;
  children: React.ReactNode;
}

function Section({ id, title, children }: SectionProps) {
  return (
    <section id={id} className="scroll-mt-28 mb-12">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-1 h-6 rounded-full flex-shrink-0" style={{ background: "linear-gradient(to bottom, #C9A84C, #E2C27A)" }} />
        <h2 className="text-xl font-bold text-[#071A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>
          {title}
        </h2>
      </div>
      <div className="text-slate-600 text-sm leading-relaxed space-y-4 pl-4">
        {children}
      </div>
    </section>
  );
}

function Para({ children }: { children: React.ReactNode }) {
  return <p className="text-slate-600 text-sm leading-relaxed">{children}</p>;
}

function Ul({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2 mt-2">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-2.5 text-sm text-slate-600">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-checkbox-circle-line text-[#C9A84C] text-xs leading-none" />
          </div>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export default function PrivacyPolicyPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="min-h-screen bg-[#EEF2F9]">
      <Navbar />

      {/* Hero */}
      <section
        className="relative w-full overflow-hidden"
        style={{
          minHeight: 300,
          background: "linear-gradient(135deg, #071A2E 0%, #0D2B4A 60%, #071A2E 100%)",
        }}
      >
        <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: "linear-gradient(90deg, transparent, #C9A84C 30%, #E2C27A 50%, #C9A84C 70%, transparent)" }} />
        <div className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ background: "linear-gradient(to bottom, transparent, #C9A84C 25%, #E2C27A 55%, #C9A84C 80%, transparent)" }} />
        <div className="absolute top-[-60px] right-[-60px] w-80 h-80 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(201,168,76,0.08) 0%, transparent 70%)" }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 pt-36 pb-14">
          <div className="flex items-center gap-2 text-xs text-white/40 mb-6">
            <Link to="/" className="hover:text-white/70 transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line leading-none" />
            <span className="text-white/70">Privacy Policy</span>
          </div>
          <span className="inline-flex items-center gap-2 bg-[#C9A84C]/15 border border-[#C9A84C]/30 text-[#C9A84C] text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-1.5 rounded-full mb-5">
            <i className="ri-shield-user-line text-xs leading-none" />
            UK GDPR Compliant
          </span>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Privacy Policy
          </h1>
          <p className="text-white/55 text-sm max-w-xl leading-relaxed">
            This policy explains how Thorpe Dellow collects, uses, and protects your personal data in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
          </p>
          <p className="text-white/35 text-xs mt-4">Last updated: 28 March 2026 &nbsp;·&nbsp; Version 1.0</p>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-10">

          {/* Sidebar TOC */}
          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-28 bg-white rounded-2xl p-6 border border-slate-100">
              <p className="text-[10px] font-bold text-[#C9A84C] uppercase tracking-widest mb-4">Contents</p>
              <nav className="flex flex-col gap-1">
                {sections.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    className="text-left text-xs text-slate-500 hover:text-[#071A2E] py-1.5 px-2 rounded-lg hover:bg-[#EEF2F9] transition-colors cursor-pointer"
                  >
                    {s.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main content */}
          <div className="lg:col-span-3 bg-white rounded-2xl p-8 md:p-10">

            <Section id="who-we-are" title="1. Who We Are">
              <Para>
                Thorpe Property Development Ltd (Company No. 07590563), trading as Thorpe Dellow (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;), is a specialist alternative investment firm registered in England and Wales. Our registered office is at One Embankment, Leeds LS1 4DW, United Kingdom.
              </Para>
              <Para>
                For the purposes of the UK GDPR and the Data Protection Act 2018, Thorpe Dellow acts as the <strong className="text-[#071A2E]">Data Controller</strong> for personal data collected through this website and our investment operations.
              </Para>
              <Para>
                If you have any questions regarding this policy or our data practices, please contact our Data Protection contact at: <a href="mailto:compliance@thorpedellow.com" className="text-[#C9A84C] hover:underline">compliance@thorpedellow.com</a>.
              </Para>
            </Section>

            <Section id="data-we-collect" title="2. Data We Collect">
              <Para>We may collect the following categories of personal data:</Para>
              <div className="space-y-4 mt-2">
                {[
                  { title: "Identity Data", items: ["Full name", "Date of birth (where investor eligibility verification is required)", "Proof of identity documentation"] },
                  { title: "Contact Data", items: ["Email address", "Telephone number", "Postal address"] },
                  { title: "Financial & Investment Data", items: ["Indicative investment amount", "Investor classification (HNW, sophisticated, retail)", "Source of funds information", "Bank account details (for payment processing)"] },
                  { title: "Technical Data", items: ["IP address", "Browser type and version", "Time zone and location", "Operating system and platform", "Cookies and similar tracking data"] },
                  { title: "Communications Data", items: ["Messages and enquiries sent via our website forms", "Email correspondence with our team"] },
                ].map((cat) => (
                  <div key={cat.title} className="bg-[#EEF2F9] rounded-xl px-5 py-4">
                    <p className="text-xs font-bold text-[#071A2E] uppercase tracking-wide mb-2">{cat.title}</p>
                    <Ul items={cat.items} />
                  </div>
                ))}
              </div>
            </Section>

            <Section id="how-we-collect" title="3. How We Collect Data">
              <Para>We collect personal data through the following means:</Para>
              <Ul items={[
                "Directly from you when you complete our website enquiry forms, expression of interest forms, or Information Memorandum access forms",
                "Via email or telephone correspondence with our investment team",
                "Automatically through cookies and tracking technologies when you visit our website",
                "From third parties, including identity verification providers and credit reference agencies (where applicable)",
              ]} />
            </Section>

            <Section id="how-we-use" title="4. How We Use Your Data">
              <Para>We use your personal data for the following purposes:</Para>
              <Ul items={[
                "To respond to your investment enquiries and expressions of interest",
                "To assess your eligibility as a qualifying investor under FSMA 2000",
                "To fulfil our obligations under the Financial Services and Markets Act 2000 and related regulations",
                "To send you investment updates, product documentation, and information about Thorpe Dellow offerings (where you have consented)",
                "To verify your identity and conduct anti-money laundering (AML) and know-your-customer (KYC) checks",
                "To manage and administer your investment if you proceed to invest",
                "To improve our website and services through analytics",
                "To comply with legal and regulatory obligations",
              ]} />
            </Section>

            <Section id="legal-basis" title="5. Legal Basis for Processing">
              <Para>We process your personal data on the following legal bases:</Para>
              <div className="space-y-3 mt-2">
                {[
                  { basis: "Consent", desc: "Where you have opted in to receive marketing communications or provided explicit consent for a specific processing activity. You may withdraw consent at any time." },
                  { basis: "Legitimate Interests", desc: "Where processing is necessary for our legitimate business interests (e.g. responding to enquiries, preventing fraud, improving our services), provided those interests are not overridden by your rights." },
                  { basis: "Legal Obligation", desc: "Where we are required to process data to comply with applicable laws and regulations, including FSMA 2000, AML legislation, and HMRC requirements." },
                  { basis: "Contract", desc: "Where processing is necessary for the performance of a contract with you (e.g. managing your investment subscription)." },
                ].map((b) => (
                  <div key={b.basis} className="flex gap-3 border-l-2 border-[#C9A84C]/40 pl-4 py-1">
                    <div>
                      <p className="text-xs font-bold text-[#071A2E] mb-0.5">{b.basis}</p>
                      <p className="text-xs text-slate-500 leading-relaxed">{b.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            <Section id="sharing" title="6. Sharing Your Data">
              <Para>We do not sell your personal data. We may share your data with:</Para>
              <Ul items={[
                "Our independent Security Trustee, for the administration of investor security arrangements",
                "Legal and compliance advisers, for the purpose of regulatory compliance",
                "Identity verification and AML service providers",
                "HMRC and other regulatory authorities, where required by law",
                "IT service providers and web hosting partners (acting as data processors under our instruction)",
                "Successor entities in the event of a corporate restructure or acquisition",
              ]} />
              <Para>
                All third parties are required to process your data securely and in accordance with applicable data protection law. We do not permit third parties to use your data for their own marketing purposes.
              </Para>
            </Section>

            <Section id="retention" title="7. Retention Periods">
              <Para>We retain your personal data for as long as necessary for the purposes set out in this policy, and in any event for no longer than:</Para>
              <div className="overflow-x-auto mt-2">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-[#071A2E] text-white">
                      <th className="text-left px-4 py-2.5 rounded-tl-lg font-semibold">Data Type</th>
                      <th className="text-left px-4 py-2.5 rounded-tr-lg font-semibold">Retention Period</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Website enquiry / contact form data", "3 years from date of submission"],
                      ["Investor application & KYC documents", "6 years from end of investment term"],
                      ["Active investor records", "Duration of investment + 6 years"],
                      ["AML / KYC records", "5 years from the end of the business relationship"],
                      ["Accounting and financial records", "6 years (HMRC requirement)"],
                      ["Marketing consent records", "Until withdrawn + 12 months"],
                    ].map(([type, period], i) => (
                      <tr key={type} className={i % 2 === 0 ? "bg-[#EEF2F9]" : "bg-white"}>
                        <td className="px-4 py-2.5 text-slate-600">{type}</td>
                        <td className="px-4 py-2.5 text-[#071A2E] font-medium">{period}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            <Section id="your-rights" title="8. Your Rights">
              <Para>Under the UK GDPR, you have the following rights regarding your personal data:</Para>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                {[
                  { icon: "ri-eye-line", right: "Right of Access", desc: "Request a copy of personal data we hold about you (Subject Access Request)." },
                  { icon: "ri-edit-box-line", right: "Right to Rectification", desc: "Request correction of inaccurate or incomplete data." },
                  { icon: "ri-delete-bin-6-line", right: "Right to Erasure", desc: "Request deletion of your data, subject to legal retention obligations." },
                  { icon: "ri-pause-circle-line", right: "Right to Restriction", desc: "Request that we restrict processing of your data in certain circumstances." },
                  { icon: "ri-swap-line", right: "Right to Portability", desc: "Receive your data in a structured, machine-readable format." },
                  { icon: "ri-thumb-down-line", right: "Right to Object", desc: "Object to processing based on legitimate interests or for direct marketing." },
                ].map((r) => (
                  <div key={r.right} className="bg-[#EEF2F9] rounded-xl p-4 flex gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C9A84C]/15 flex-shrink-0">
                      <i className={`${r.icon} text-[#C9A84C] text-sm leading-none`} />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-[#071A2E] mb-0.5">{r.right}</p>
                      <p className="text-xs text-slate-500 leading-relaxed">{r.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Para>
                To exercise any of these rights, please contact us at <a href="mailto:compliance@thorpedellow.com" className="text-[#C9A84C] hover:underline">compliance@thorpedellow.com</a>. We will respond within one calendar month. You also have the right to lodge a complaint with the <strong className="text-[#071A2E]">Information Commissioner&apos;s Office (ICO)</strong> at <a href="https://ico.org.uk" target="_blank" rel="nofollow noreferrer" className="text-[#C9A84C] hover:underline">ico.org.uk</a>.
              </Para>
            </Section>

            <Section id="cookies" title="9. Cookies">
              <Para>
                Our website uses cookies and similar tracking technologies to improve functionality and analyse usage. Cookies are small text files stored on your device when you visit our website.
              </Para>
              <div className="space-y-3 mt-3">
                {[
                  { type: "Strictly Necessary", desc: "Essential for the website to function. Cannot be disabled.", color: "bg-green-50 border-green-200 text-green-700" },
                  { type: "Analytics Cookies", desc: "Help us understand how visitors interact with our website (e.g. Google Analytics). Data is anonymised where possible.", color: "bg-[#EEF2F9] border-slate-200 text-slate-600" },
                  { type: "Preference Cookies", desc: "Remember your settings and preferences to improve your experience.", color: "bg-[#EEF2F9] border-slate-200 text-slate-600" },
                ].map((c) => (
                  <div key={c.type} className={`rounded-xl px-4 py-3 border ${c.color}`}>
                    <p className="text-xs font-bold mb-0.5">{c.type}</p>
                    <p className="text-xs leading-relaxed">{c.desc}</p>
                  </div>
                ))}
              </div>
              <Para>
                You can manage or disable cookies through your browser settings. Note that disabling certain cookies may affect website functionality.
              </Para>
            </Section>

            <Section id="transfers" title="10. International Transfers">
              <Para>
                Your personal data is primarily stored and processed within the United Kingdom. Where we engage service providers outside the UK, we ensure adequate safeguards are in place, including UK International Data Transfer Agreements (IDTAs) or reliance on adequacy decisions, to protect your data to the same standard as required under UK GDPR.
              </Para>
            </Section>

            <Section id="changes" title="11. Changes to This Policy">
              <Para>
                We may update this Privacy Policy periodically to reflect changes in our practices or applicable law. The &quot;Last updated&quot; date at the top of this page indicates when the policy was last revised. We encourage you to review this policy regularly. Material changes will be notified via email to investors where appropriate.
              </Para>
            </Section>

            <Section id="contact" title="12. Contact Us">
              <Para>If you have questions, concerns, or wish to exercise your data protection rights, please contact us:</Para>
              <div className="bg-[#071A2E] rounded-2xl p-6 mt-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {[
                    { icon: "ri-mail-line", label: "Email", value: "compliance@thorpedellow.com", href: "mailto:compliance@thorpedellow.com" },
                    { icon: "ri-map-pin-2-line", label: "Post", value: "One Embankment, Leeds LS1 4DW, United Kingdom" },
                    { icon: "ri-phone-line", label: "Telephone", value: "+44 20 3097 6185", href: "tel:+442030976185" },
                  ].map((c) => (
                    <div key={c.label} className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C9A84C]/15 border border-[#C9A84C]/25 flex-shrink-0">
                        <i className={`${c.icon} text-[#C9A84C] text-sm leading-none`} />
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-0.5">{c.label}</p>
                        {c.href ? (
                          <a href={c.href} className="text-white text-xs font-medium hover:text-[#C9A84C] transition-colors">{c.value}</a>
                        ) : (
                          <p className="text-white text-xs font-medium">{c.value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Section>

            {/* Bottom nav */}
            <div className="border-t border-slate-100 pt-8 flex flex-col sm:flex-row gap-3 justify-between items-center">
              <p className="text-xs text-slate-400">Last updated: 28 March 2026</p>
              <div className="flex gap-3">
                <Link to="/terms-of-use" className="text-xs text-[#C9A84C] hover:underline cursor-pointer whitespace-nowrap">Terms of Use</Link>
                <span className="text-slate-300">·</span>
                <Link to="/risk-disclaimer" className="text-xs text-[#C9A84C] hover:underline cursor-pointer whitespace-nowrap">Risk Disclaimer</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
